import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Typography, Avatar, IconButton, Tooltip, TablePagination
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatDate, getAvatarUrl } from '../../../utils/helpers';
import adminService from '../../../services/adminService';
import AdminToolbar from '../components/AdminToolbar';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('all');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [search, filterRole]);

    useEffect(() => {
        fetchUsers();
    }, [search, filterRole, page, rowsPerPage]);

    const fetchUsers = async () => {
        const params = {
            page: page + 1,
            limit: rowsPerPage
        };
        if (search) params.search = search;
        if (filterRole !== 'all') params.role = filterRole;

        const res = await adminService.getAllUsers(params);
        if (res.success) {
            setUsers(res.data);
            setTotalRows(res.total || 0);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleToggleStatus = async (user) => {
        if (window.confirm(`Bạn có chắc muốn ${user.isActive ? 'KHÓA' : 'MỞ KHÓA'} tài khoản ${user.username}?`)) {
            try {
                await adminService.toggleUserStatus(user.id, !user.isActive);
                fetchUsers();
            } catch (error) {
                alert('Lỗi: ' + error.message);
            }
        }
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Quản Lý Người Dùng</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterRole}
                onFilterChange={setFilterRole}
                filterOptions={[
                    { value: 'all', label: 'Tất cả Vai trò' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'user', label: 'User' }
                ]}
                placeholder="Tìm tên hoặc email..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>User</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Email / Phone</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Vai Trò</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Tạo</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Hành Động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell sx={{ color: 'white' }}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar src={getAvatarUrl(user.avatar)} alt={user.username}>{user.username?.charAt(0)}</Avatar>
                                        <Box>
                                            <Typography fontWeight="bold">{user.username}</Typography>
                                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{user.id}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    <Typography variant="body2">{user.email}</Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{user.phone || '-'}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {user.role === 'admin' ?
                                        <Chip label="Admin" color="secondary" size="small" /> :
                                        <Chip label="User" variant="outlined" sx={{ color: '#94a3b8', borderColor: '#475569' }} size="small" />
                                    }
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {formatDate(user.createdAt)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.isActive ? "Hoạt động" : "Đã khóa"}
                                        color={user.isActive ? "success" : "error"}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={user.isActive ? "Khóa tài khoản" : "Mở khóa tài khoản"}>
                                        <IconButton
                                            onClick={() => handleToggleStatus(user)}
                                            color={user.isActive ? "error" : "success"}
                                        >
                                            {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ color: 'white' }}
            />
        </Box>
    );
};

export default UserManagement;
